import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getUnitStateListener} from './../../../hooks/unitDataHooks';
import './UnitCard.css';
import { useSecondHeader } from './../../../contexts/SecondHeaderContext';
import { useTranslation } from 'react-i18next';
import { isDangerousWasteDeploymentUnit } from '../../../common/deploymentCategory.js';
import { getActiveUnitIdsForDisplayUnit } from '../../../common/displayUnitRanges.js';

const STATUS_RECENCY_WINDOW_MS = 60 * 60 * 1000;

const toTimestampMs = (value) => {
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim()) {
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) return numericValue;
    }
    if (value && typeof value.toMillis === 'function') return value.toMillis();
    if (value && typeof value.toDate === 'function') {
        const dateValue = value.toDate();
        return dateValue instanceof Date ? dateValue.getTime() : Number.NaN;
    }
    return Number.NaN;
};

const getSeverityForState = (state) => {
    switch (state) {
        case 'Error':
            return 3;
        case 'Warning':
            return 2;
        case 'Ready':
            return 1;
        default:
            return 1;
    }
};

function UnitCard({ display_unit, unit, displayUnitIndex }) {
    const { t } = useTranslation();
    const { locationId } = useParams();
    const [liveColor, setLiveColor] = useState('var(--gray-500)');
    const navigate = useNavigate();
    const { setCurrentUnit, setLatestUseString } = useSecondHeader();

    // Determine which unit data to use
    // If display_unit is provided, use it; otherwise use the legacy unit object
    const unitData = display_unit || unit || null;
    
    // Only subscribe to currently active ranges for display units.
    // This prevents expired unit ranges from affecting the live status indicator.
    const activeDisplayUnitIds = getActiveUnitIdsForDisplayUnit(display_unit);
    const unitIds = activeDisplayUnitIds.length > 0
        ? [...new Set(activeDisplayUnitIds)]
        : (unit?.unit_id ? [unit.unit_id] : []);
    
    // For navigation: use displayUnitIndex for display_units, otherwise use unit_id
    const navigationIdentifier = display_unit !== null && displayUnitIndex !== null 
        ? displayUnitIndex 
        : (unit?.unit_id || null);
    
    // Get display properties
    const displayName = unitData?.display_name || '';
    const posterUrl = unitData?.poster_url || '';
    const activations = unitData?.activations || 0;
    const latestUse = unitData?.latest_use || '';

    useEffect(() => {
        if (unitIds.length === 0) return;

        // Track states for all units
        const unitStates = {};
        const unsubscribeCallbacks = [];

        // Helper function to set color based on worst state
        const updateColor = () => {
            const recentUnitStates = Object.values(unitStates).filter(
                (unitState) =>
                    Number.isFinite(unitState.timestampMs) &&
                    unitState.timestampMs >= Date.now() - STATUS_RECENCY_WINDOW_MS,
            );

            if (recentUnitStates.length === 0) {
                setLiveColor('var(--gray-500)');
                return;
            }

            const worstRecentState = recentUnitStates.reduce((worstState, unitState) => {
                return getSeverityForState(unitState.state) > getSeverityForState(worstState)
                    ? unitState.state
                    : worstState;
            }, 'Ready');

            switch (worstRecentState) {
                case 'Error':
                    setLiveColor('var(--error-500)');
                    break;
                case 'Warning':
                    setLiveColor('var(--warning-500)');
                    break;
                default:
                    setLiveColor('var(--success-500)');
                    break;
            }
        };

        // Set up listeners for all units
        unitIds.forEach(unitId => {
            const unsubscribe = getUnitStateListener(unitId, {
                next: (log) => {
                    console.log("Unit state log:", log);
                    const timestampMs = toTimestampMs(log.timestamp);
                    const isRecent = Number.isFinite(timestampMs)
                        && timestampMs >= Date.now() - STATUS_RECENCY_WINDOW_MS;
                    unitStates[unitId] = {
                        timestampMs,
                        state: isRecent ? log.state : 'Disconnected',
                    };
                    updateColor();
                },
                error: (error) => {
                    console.error(`Error in unit state listener for ${unitId}:`, error);
                }
            });
            unsubscribeCallbacks.push(unsubscribe);
        });

        return () => {
            unsubscribeCallbacks.forEach(unsubscribe => {
                if (unsubscribe) unsubscribe();
            });
        };
    }, [unitIds.join(',')]); // Re-run when unit IDs change

    const navigateToUnit = () => {
        if (navigationIdentifier === null) return;
        
        // Create unit object for navigation
        const unitForNavigation = unitData;
        console.log("navigateToUnit", unitForNavigation);
        setCurrentUnit(unitForNavigation);
        setLatestUseString(latestUse);
        if (display_unit && isDangerousWasteDeploymentUnit(display_unit)) {
            navigate(`/locations/${locationId}/unit/${navigationIdentifier}/dangerous-waste/dashboard`);
            return;
        }
        navigate(`/locations/${locationId}/unit/${navigationIdentifier}/dashboard`);
    };

    return (
        <div className="UnitCard">
            <div className="card-wrapper" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className="login-container card-container" onClick={navigateToUnit} style={{ width: '100%', maxWidth: '620px', cursor: 'pointer' }}>
                    <div className="card-header">
                        <div className="card-header-title">
                            {displayName}
                        </div>
                    </div>
                    <div className="card-body" style={{ marginBottom: 0, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
                        <figure className="img-wrapper">
                            <img className="poster-image" src={posterUrl} width="588" height="250" alt="Poster" />
                        </figure>
                    </div>
                    <div className="card-body location-bar" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                        <div className="location-bar-left">
                            <span className="units-count-text">
                                <div className="units-count-label">{t("Aktiveringer i dag")}:</div>
                                <div className="units-count-number">{activations}</div>
                            </span>
                            <span className="units-count-text">
                                <div className="units-count-label">{t("Seneste anvendelse")}:</div>
                                <div className="units-count-number">{latestUse || ''}</div>
                            </span>
                        </div>
                        <div className="location-bar-right">
                            <div className="live-activity-wrapper">
                                <div className="live-activity-dot" style={{ backgroundColor: liveColor }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UnitCard;
